import api from './api';
import { formatMoney } from '../utils/currency';
import {
  TENANT_PLAN_BILLING_INTERVALS,
  TENANT_PLAN_CURRENCY_OPTIONS,
  TENANT_PLAN_MAX_USERS_MAX,
  TENANT_PLAN_MAX_USERS_MIN,
  TENANT_PLAN_PRICE_MAX,
  TENANT_PLAN_PRICE_MIN,
  TENANT_PLAN_TRIAL_DAYS_MAX,
  TENANT_PLAN_TRIAL_DAYS_MIN,
  type TenantPlanBillingInterval,
  type TenantPlanFormTabId,
} from '../constants/tenantSubscriptionPlanConfig';

export interface TenantSubscriptionPlanRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billingInterval: TenantPlanBillingInterval | string;
  trialDays: number;
  maxUsers?: number;
  maxZones?: number;
  includedModules: string[];
  features: string[];
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
}

export interface TenantSubscriptionPlanPayload {
  code: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billingInterval: TenantPlanBillingInterval | string;
  trialDays: number;
  maxUsers?: number;
  maxZones?: number;
  includedModules: string[];
  features: string[];
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
}

export const BILLING_INTERVAL_OPTIONS = TENANT_PLAN_BILLING_INTERVALS;
export const PLAN_CURRENCY_OPTIONS = TENANT_PLAN_CURRENCY_OPTIONS;

export function extractApiError(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || 'Something went wrong';
}

export function formatPlanPrice(row: Pick<TenantSubscriptionPlanRow, 'price' | 'currency' | 'billingInterval'>) {
  if (!row.price) return 'Custom pricing';
  const cur = row.currency || undefined;
  const intervalLabel =
    BILLING_INTERVAL_OPTIONS.find((o) => o.value === row.billingInterval)?.label
    || String(row.billingInterval || 'monthly');
  if (row.billingInterval === 'lifetime') {
    return `${formatMoney(row.price, cur)} · ${intervalLabel}`;
  }
  return `${formatMoney(row.price, cur)} / ${intervalLabel}`;
}

export function formatPlanBillingLabel(billingInterval?: string) {
  return BILLING_INTERVAL_OPTIONS.find((o) => o.value === billingInterval)?.label
    || String(billingInterval || '—');
}

export async function listTenantSubscriptionPlans(params?: { active?: boolean; public?: boolean }) {
  const res = await api.get('/tenants/subscription-plans', { params });
  const rows = res.data.data as unknown[];
  return rows.map((row) => normalizePlanRow(row as Record<string, unknown>));
}

export async function createTenantSubscriptionPlan(payload: TenantSubscriptionPlanPayload) {
  const res = await api.post('/tenants/subscription-plans', payload);
  return normalizePlanRow(res.data.data as Record<string, unknown>);
}

export async function updateTenantSubscriptionPlan(id: string, payload: Partial<TenantSubscriptionPlanPayload>) {
  const res = await api.put(`/tenants/subscription-plans/${id}`, payload);
  return normalizePlanRow(res.data.data as Record<string, unknown>);
}

export async function setTenantSubscriptionPlanStatus(id: string, isActive: boolean) {
  const res = await api.patch(`/tenants/subscription-plans/${id}/status`, { isActive });
  return normalizePlanRow(res.data.data as Record<string, unknown>);
}

export async function deleteTenantSubscriptionPlan(id: string) {
  const res = await api.delete(`/tenants/subscription-plans/${id}`);
  return res.data;
}

function normalizePlanRow(row: Record<string, unknown>): TenantSubscriptionPlanRow {
  return {
    _id: String(row._id),
    code: String(row.code || ''),
    name: String(row.name || ''),
    description: row.description ? String(row.description) : '',
    price: Number(row.price) || 0,
    currency: String(row.currency || 'INR'),
    billingInterval: String(row.billingInterval || 'monthly'),
    trialDays: Number(row.trialDays) || 0,
    maxUsers: row.maxUsers != null ? Number(row.maxUsers) : undefined,
    maxZones: row.maxZones != null ? Number(row.maxZones) : undefined,
    includedModules: Array.isArray(row.includedModules) ? row.includedModules.map(String) : [],
    features: Array.isArray(row.features) ? row.features.map(String) : [],
    isActive: row.isActive !== false,
    isPublic: row.isPublic !== false,
    sortOrder: Number(row.sortOrder) || 0,
  };
}

/** Unique plan code from display name (lowercase, a-z 0-9 _ -). */
export function slugifyPlanCode(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 48);
}

/** Returns the first consecutive repeated word (case-insensitive), or null. */
export function findRepeatedWord(text: string): string | null {
  const words = text
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ''))
    .filter(Boolean);
  for (let i = 1; i < words.length; i += 1) {
    if (words[i].toLowerCase() === words[i - 1].toLowerCase()) {
      return words[i];
    }
  }
  return null;
}

export function validatePlanDescription(description?: string): string | undefined {
  if (!description?.trim()) return undefined;
  const repeated = findRepeatedWord(description);
  if (repeated) return `Description cannot contain repeated words ("${repeated}")`;
  return undefined;
}

export function validatePlanHighlights(features: string[] | string): string | undefined {
  const text = Array.isArray(features) ? features.join('\n') : features;
  if (!text.trim()) return undefined;
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    const repeated = findRepeatedWord(line);
    if (repeated) return `Plan highlights cannot contain repeated words ("${repeated}")`;
  }
  return undefined;
}

export function isDuplicatePlanCode(
  code: string,
  existingCodes: string[],
  options?: { excludeCode?: string },
): boolean {
  const normalized = code.trim().toLowerCase();
  if (!normalized) return false;
  const exclude = options?.excludeCode?.trim().toLowerCase();
  return existingCodes.some((c) => {
    const other = c.trim().toLowerCase();
    if (!other) return false;
    if (exclude && other === exclude) return false;
    return other === normalized;
  });
}

export function emptyPlanForm(overrides?: Partial<TenantSubscriptionPlanPayload>): TenantSubscriptionPlanPayload {
  return {
    code: '',
    name: '',
    description: '',
    price: 0,
    currency: 'INR',
    billingInterval: 'monthly',
    trialDays: 14,
    maxUsers: undefined,
    maxZones: undefined,
    includedModules: [],
    features: [],
    isActive: true,
    isPublic: true,
    sortOrder: 0,
    ...overrides,
  };
}

export function planRowToForm(row: TenantSubscriptionPlanRow): TenantSubscriptionPlanPayload {
  return emptyPlanForm({
    code: row.code,
    name: row.name,
    description: row.description || '',
    price: row.price ?? 0,
    currency: row.currency || 'INR',
    billingInterval: row.billingInterval || 'monthly',
    trialDays: row.trialDays ?? 0,
    maxUsers: row.maxUsers,
    maxZones: row.maxZones,
    includedModules: row.includedModules || [],
    features: row.features || [],
    isActive: row.isActive !== false,
    isPublic: row.isPublic !== false,
    sortOrder: row.sortOrder ?? 0,
  });
}

export function validatePlanForm(
  form: TenantSubscriptionPlanPayload,
  tabId?: TenantPlanFormTabId,
  options?: { existingCodes?: string[]; excludeCode?: string; mode?: 'create' | 'edit' },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const checkInfo = !tabId || tabId === 'info';
  const checkPricing = !tabId || tabId === 'pricing';
  const checkModules = !tabId || tabId === 'modules';

  if (checkInfo) {
    if (!form.code.trim()) errors.code = 'Plan code is required';
    else if (!/^[a-z0-9_-]+$/.test(form.code.trim())) errors.code = 'Use lowercase letters, numbers, hyphen, or underscore';
    else if (
      options?.mode === 'create'
      && options.existingCodes
      && isDuplicatePlanCode(form.code, options.existingCodes, { excludeCode: options.excludeCode })
    ) {
      errors.code = 'This plan code is already used';
    }
    if (!form.name.trim()) errors.name = 'Plan name is required';
    else if (form.name.trim().length < 2) errors.name = 'Plan name must be at least 2 characters';
    const descriptionError = validatePlanDescription(form.description);
    if (descriptionError) errors.description = descriptionError;
  }

  if (checkPricing) {
    if (!Number.isFinite(form.price) || form.price < TENANT_PLAN_PRICE_MIN) {
      errors.price = 'Price cannot be negative';
    } else if (form.price > TENANT_PLAN_PRICE_MAX) {
      errors.price = `Price cannot exceed ${TENANT_PLAN_PRICE_MAX.toLocaleString('en-IN')}`;
    }
    if (!form.billingInterval?.trim()) {
      errors.billingInterval = 'Billing cycle is required';
    } else if (!BILLING_INTERVAL_OPTIONS.some((o) => o.value === form.billingInterval)) {
      errors.billingInterval = 'Select a valid billing cycle';
    }
    if (!Number.isFinite(form.trialDays) || form.trialDays < TENANT_PLAN_TRIAL_DAYS_MIN) {
      errors.trialDays = `Trial days must be at least ${TENANT_PLAN_TRIAL_DAYS_MIN}`;
    } else if (form.trialDays > TENANT_PLAN_TRIAL_DAYS_MAX) {
      errors.trialDays = `Trial days cannot exceed ${TENANT_PLAN_TRIAL_DAYS_MAX}`;
    }
    if (form.maxUsers != null) {
      if (!Number.isFinite(form.maxUsers) || form.maxUsers < TENANT_PLAN_MAX_USERS_MIN) {
        errors.maxUsers = `Max users must be at least ${TENANT_PLAN_MAX_USERS_MIN}`;
      } else if (form.maxUsers > TENANT_PLAN_MAX_USERS_MAX) {
        errors.maxUsers = `Max users cannot exceed ${TENANT_PLAN_MAX_USERS_MAX.toLocaleString('en-IN')}`;
      }
    }
  }

  if (checkModules) {
    const highlightsError = validatePlanHighlights(form.features);
    if (highlightsError) errors.features = highlightsError;
  }

  return errors;
}

export function buildPlanPayload(form: TenantSubscriptionPlanPayload): TenantSubscriptionPlanPayload {
  return {
    code: form.code.trim().toLowerCase(),
    name: form.name.trim(),
    description: form.description?.trim() || undefined,
    price: Number(form.price) || 0,
    currency: form.currency || 'INR',
    billingInterval: form.billingInterval || 'monthly',
    trialDays: Number(form.trialDays) || 0,
    maxUsers: form.maxUsers != null ? Number(form.maxUsers) : undefined,
    maxZones: form.maxZones != null ? Number(form.maxZones) : undefined,
    includedModules: form.includedModules,
    features: form.features.filter(Boolean),
    isActive: form.isActive,
    isPublic: form.isPublic,
    sortOrder: Number(form.sortOrder) || 0,
  };
}
